import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
import SupplierCertificationsPage from './pages/SupplierCertificationsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import QualityDashboardPage from './pages/QualityDashboardPage';
import QmsPlansPage from './pages/QmsPlansPage';
import QmsPlanDetailPage from './pages/QmsPlanDetailPage';
import QmsChecksPage from './pages/QmsChecksPage';
import QmsDashboardPage from './pages/QmsDashboardPage';
import ShipmentRegistryPage from './pages/ShipmentRegistryPage';
import QmsProductionRequestsPage from './pages/QmsProductionRequestsPage';
import TreasuryPage from './pages/TreasuryPage';
import ApqpProjectsPage from './pages/ApqpProjectsPage';
import ApqpProjectDetailPage from './pages/ApqpProjectDetailPage';
import ApqpDashboardPage from './pages/ApqpDashboardPage';
import InstrumentsPage from './pages/InstrumentsPage';
import InstrumentDetailPage from './pages/InstrumentDetailPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderProductMatrixPage from './pages/OrderProductMatrixPage';
import ProductionLotsPage from './pages/ProductionLotsPage';
import LotDetailPage from './pages/LotDetailPage';
import NonConformitiesPage from './pages/NonConformitiesPage';
import NCDetailPage from './pages/NCDetailPage';
import ConfigurationDetailPage from './pages/ConfigurationDetailPage';
import UsersPage from './pages/UsersPage';
import ManualPage from './pages/ManualPage';
import PlanningPage from './pages/PlanningPage';
import MachinesPage from './pages/MachinesPage';
import MachineDetailPage from './pages/MachineDetailPage';
import MaintenancePage from './pages/MaintenancePage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import SkillsMatrixPage from './pages/SkillsMatrixPage';
import CalendarioCorsiPage from './pages/CalendarioCorsiPage';
import RiepilogoOreFormazionePage from './pages/RiepilogoOreFormazionePage';
import VerbaliFormazionePage from './pages/VerbaliFormazionePage';
import VerbaleDetailPage from './pages/VerbaleDetailPage';
import KPIDashboardPage from './pages/KPIDashboardPage';
import RevenueKPIPage from './pages/RevenueKPIPage';
import PersonnelKPIPage from './pages/PersonnelKPIPage';
import NCCustomerKPIPage from './pages/NCCustomerKPIPage';
import ScrapKPIPage from './pages/ScrapKPIPage';
import ShipmentCalendarPage from './pages/ShipmentCalendarPage';
import OffersListPage from './pages/OffersListPage';
import OfferDetailPage from './pages/OfferDetailPage';
import CommercialDashboardPage from './pages/CommercialDashboardPage';
import EmailSettingsPage from './pages/EmailSettingsPage';
import EnvironmentResetPage from './pages/EnvironmentResetPage';
import InternalScrapPage from './pages/InternalScrapPage';
import CustomerReceivedNcsPage from './pages/CustomerReceivedNcsPage';
import CustomerReceivedNcDetailPage from './pages/CustomerReceivedNcDetailPage';
import CustomerReceivedNcDashboardPage from './pages/CustomerReceivedNcDashboardPage';
import QaSignatureSettingsPage from './pages/QaSignatureSettingsPage';
import SupplierNcsPage from './pages/SupplierNcsPage';
import SupplierNcDetailPage from './pages/SupplierNcDetailPage';
import SupplierEvaluationDashboardPage from './pages/SupplierEvaluationDashboardPage';
import DebitNoteDetailPage from './pages/DebitNoteDetailPage';
import SupplierPortal8DPage from './pages/SupplierPortal8DPage';
import QualityControlReportsPage from './pages/QualityControlReportsPage';
import QualityControlReportDetailPage from './pages/QualityControlReportDetailPage';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/supplier-portal/8d/:token" element={<SupplierPortal8DPage />} />
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
          <Route path="/suppliers/certifications" element={<ProtectedRoute><SupplierCertificationsPage /></ProtectedRoute>} />
          <Route path="/suppliers/:id" element={<ProtectedRoute><SupplierDetailPage /></ProtectedRoute>} />
          <Route path="/audit-trail" element={<ProtectedRoute><AuditTrailPage /></ProtectedRoute>} />
          <Route path="/quality-dashboard" element={<ProtectedRoute><QualityDashboardPage /></ProtectedRoute>} />
          <Route path="/qms-plans" element={<ProtectedRoute><QmsPlansPage /></ProtectedRoute>} />
          <Route path="/qms-plans/:id" element={<ProtectedRoute><QmsPlanDetailPage /></ProtectedRoute>} />
          <Route path="/qms-checks" element={<ProtectedRoute><QmsChecksPage /></ProtectedRoute>} />
          <Route path="/qms-dashboard" element={<ProtectedRoute><QmsDashboardPage /></ProtectedRoute>} />
          <Route path="/shipment-registry" element={<ProtectedRoute><ShipmentRegistryPage /></ProtectedRoute>} />
          <Route path="/qms-production-requests" element={<ProtectedRoute><QmsProductionRequestsPage /></ProtectedRoute>} />
          {process.env.REACT_APP_ENABLE_TREASURY === 'true' && (
            <Route path="/treasury" element={<ProtectedRoute><TreasuryPage /></ProtectedRoute>} />
          )}
          <Route path="/apqp-projects" element={<ProtectedRoute><ApqpProjectsPage /></ProtectedRoute>} />
          <Route path="/apqp-projects/:id" element={<ProtectedRoute><ApqpProjectDetailPage /></ProtectedRoute>} />
          <Route path="/apqp-dashboard" element={<ProtectedRoute><ApqpDashboardPage /></ProtectedRoute>} />
          <Route path="/instruments" element={<ProtectedRoute><InstrumentsPage /></ProtectedRoute>} />
          <Route path="/instruments/:id" element={<ProtectedRoute><InstrumentDetailPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
          <Route path="/products/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/orders/product-matrix" element={<ProtectedRoute><OrderProductMatrixPage /></ProtectedRoute>} />
          <Route path="/orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
          <Route path="/production-lots" element={<ProtectedRoute><ProductionLotsPage /></ProtectedRoute>} />
          <Route path="/production-lots/:id" element={<ProtectedRoute><LotDetailPage /></ProtectedRoute>} />
          <Route path="/non-conformities" element={<ProtectedRoute><NonConformitiesPage /></ProtectedRoute>} />
          <Route path="/non-conformities/:id" element={<ProtectedRoute><NCDetailPage /></ProtectedRoute>} />
          <Route path="/configurations/:id" element={<ProtectedRoute><ConfigurationDetailPage /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
          <Route path="/planning" element={<ProtectedRoute><PlanningPage /></ProtectedRoute>} />
          <Route path="/machines" element={<ProtectedRoute><MachinesPage /></ProtectedRoute>} />
          <Route path="/machines/:id" element={<ProtectedRoute><MachineDetailPage /></ProtectedRoute>} />
          <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute><EmployeesPage /></ProtectedRoute>} />
          <Route path="/employees/:id" element={<ProtectedRoute><EmployeeDetailPage /></ProtectedRoute>} />
          <Route path="/skills-matrix" element={<ProtectedRoute><SkillsMatrixPage /></ProtectedRoute>} />
          <Route path="/training-calendar" element={<ProtectedRoute><CalendarioCorsiPage /></ProtectedRoute>} />
          <Route path="/training-summary" element={<ProtectedRoute><RiepilogoOreFormazionePage /></ProtectedRoute>} />
          <Route path="/training-minutes" element={<ProtectedRoute><VerbaliFormazionePage /></ProtectedRoute>} />
          <Route path="/training-minutes/:id" element={<ProtectedRoute><VerbaleDetailPage /></ProtectedRoute>} />
          <Route path="/kpi-dashboard" element={<ProtectedRoute><KPIDashboardPage /></ProtectedRoute>} />
          <Route path="/indicator-revenue" element={<ProtectedRoute><RevenueKPIPage /></ProtectedRoute>} />
          <Route path="/indicator-personnel" element={<ProtectedRoute><PersonnelKPIPage /></ProtectedRoute>} />
          <Route path="/indicator-nc-customer" element={<ProtectedRoute><NCCustomerKPIPage /></ProtectedRoute>} />
          <Route path="/indicator-scrap" element={<ProtectedRoute><ScrapKPIPage /></ProtectedRoute>} />
          <Route path="/shipment-calendar" element={<ProtectedRoute><ShipmentCalendarPage /></ProtectedRoute>} />
          <Route path="/commercial-offers" element={<ProtectedRoute><OffersListPage /></ProtectedRoute>} />
          <Route path="/commercial-offers/:id" element={<ProtectedRoute><OfferDetailPage /></ProtectedRoute>} />
          <Route path="/commercial-dashboard" element={<ProtectedRoute><CommercialDashboardPage /></ProtectedRoute>} />
          <Route path="/email-settings" element={<ProtectedRoute><EmailSettingsPage /></ProtectedRoute>} />
          <Route path="/debit-notes/:id" element={<ProtectedRoute><DebitNoteDetailPage /></ProtectedRoute>} />
          <Route path="/manuale" element={<ProtectedRoute><ManualPage /></ProtectedRoute>} />
          <Route path="/environment-reset" element={<ProtectedRoute><EnvironmentResetPage /></ProtectedRoute>} />
          <Route path="/internal-scrap" element={<ProtectedRoute><InternalScrapPage /></ProtectedRoute>} />
          <Route path="/customer-received-ncs" element={<ProtectedRoute><CustomerReceivedNcsPage /></ProtectedRoute>} />
          <Route path="/customer-received-ncs/dashboard" element={<ProtectedRoute><CustomerReceivedNcDashboardPage /></ProtectedRoute>} />
          <Route path="/customer-received-ncs/:id" element={<ProtectedRoute><CustomerReceivedNcDetailPage /></ProtectedRoute>} />
          <Route path="/qa-signature-settings" element={<ProtectedRoute><QaSignatureSettingsPage /></ProtectedRoute>} />
          <Route path="/supplier-ncs" element={<ProtectedRoute><SupplierNcsPage /></ProtectedRoute>} />
          <Route path="/supplier-ncs/evaluation-dashboard" element={<ProtectedRoute><SupplierEvaluationDashboardPage /></ProtectedRoute>} />
          <Route path="/supplier-ncs/:id" element={<ProtectedRoute><SupplierNcDetailPage /></ProtectedRoute>} />
          <Route path="/quality-control-reports" element={<ProtectedRoute><QualityControlReportsPage /></ProtectedRoute>} />
          <Route path="/quality-control-reports/:id" element={<ProtectedRoute><QualityControlReportDetailPage /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
